import {useState} from "react";
import {Icons} from "../constants/svgIcons";
import {useTranslation} from "react-i18next";
import Turnstile from "react-turnstile";
import emailjs from "@emailjs/browser";
import settings from "../constants/settings";

const ContactPage = ( ) => {
  const {t , i18n } = useTranslation();
  const lang = (key) => t(`contactPage.${key}`);
  const isRTL = i18n.language === 'ar' ? "rtl" : "ltr";

  const [formData, setFormData] = useState({
    // name: 'Max Test',
    // email: 'max@gmail.com',
    // phone: '055523551',
    // service: 'Web Application Development',
    // message: 'For testing the contact form. Please ignore.',
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [messageColor, setMessageColor] = useState('');
  const [token, setToken] = useState("");
  const [turnstileKey, setTurnstileKey] = useState(0);

  // REPLACE resetTurnstile with this simpler version
  const resetTurnstile = () => {
    setTurnstileKey(prev => prev + 1); // This will remount the component
    setToken('');
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('common.fieldRequired');
    if (!formData.email.trim()) newErrors.email = t('common.fieldRequired');
    if (!formData.phone.trim()) newErrors.phone = t('common.fieldRequired');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'This field is invalid';
    if (!formData.service.trim()) newErrors.service = t('common.fieldRequired');
    if (!formData.message.trim()) newErrors.message = t('common.fieldRequired');
    if (!token) newErrors.verification = 'Please complete the verification';
    return newErrors;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id.toLowerCase()]: e.target.value });
  };

  // Helper functions
  const showStatusMessage = (message, type) => {
    setErrors({}); // Clear previous errors
    setMessageColor(type === 'success' ? 'text-green-600' : 'text-red-600');
    setSuccessMsg(message);

    // Clear message after 5 seconds
    const timer = setTimeout(() => {
      setSuccessMsg('');
      setMessageColor('');
      clearTimeout(timer);
    }, 5000);
  };

  const robotCheck = async () => {
    try {
      // const verifyResponse = await fetch('https://robot-check.yellaung.workers.dev', {
      const verifyResponse = await fetch('https://majtech.yellaung.workers.dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token, // Only send token
        })
      });

      if (!verifyResponse.ok) {
        console.error('Verification request failed:', verifyResponse.status);
        return { success: false };
      }
      return await verifyResponse.json();
    } catch (error) {
      console.error('Robot check error:', error);
      return { success: false };
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false)
      // resetTurnstile()
      return;
    }
    const robotCheckResult = await robotCheck();
    if (!robotCheckResult.success) { // FIX: Check .success property
      setErrors({message: "Verification failed. Please try again."});
      setIsSubmitting(false)
      resetTurnstile()
      return;
    }
    try {
      await emailjs.send(
        settings.emailService.serviceId,
        settings.emailService.templateId,
        formData,
        { publicKey: settings.emailService.publicKey }
      );
      showStatusMessage('Your message has been sent! We will get back to you soon.', 'success');
      setFormData({ name: '', phone: '', email: '', service: '', message: '' });
      resetTurnstile()
    } catch (err) {
      showStatusMessage('Something went wrong. Please try again later.', 'error');
      resetTurnstile()
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div dir={isRTL ? 'ltr' : 'rtl'}>
      <section className="bg-theme-color pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">{lang('title')}</h1>
          <p className="text-white/80 text-xl max-w-2xl mx-auto">{lang('subtitle')}</p>
        </div>
      </section>
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Info */}
            <div className="space-y-6">
              <div className="bg-white rounded-3xl p-5 shadow-xl border border-gray-300">
                <h3 className="text-2xl font-black text-gray-900 mb-6">{lang('info.title')}</h3>
                {[
                  { icon: <Icons.MapPin />, text: lang('info.address') },
                  // { icon: <Icons.Phone />, text: lang('info.phone') },
                  { icon: <Icons.Mail />, text: lang('info.email') },
                  { icon: <Icons.Clock />, text: lang('info.hours') },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 mb-4 last:mb-0">
                    <div className="w-10 h-10 bg-theme-color/10 text-theme-color rounded-xl flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <span className="text-gray-700 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>

              <div className="bg-theme-color rounded-3xl p-5 text-white">
                <div className="text-4xl mb-4">🌍</div>
                <h4 className="font-black text-xl mb-2">GCC & Worldwide</h4>
                <p className="text-white/80 text-sm leading-relaxed">
                  UAE • Saudi • Qatar • Kuwait • Bahrain • Oman & beyond
                </p>
              </div>
            </div>
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl p-5 shadow-xl border border-gray-300">
                {/*{isSubmitting ? (*/}
                {/*  <div className="text-center py-16">*/}
                {/*    <div className="text-6xl mb-4">✅</div>*/}
                {/*    <h3 className="text-2xl font-black text-gray-900 mb-2">*/}
                {/*      Message Sent!*/}
                {/*    </h3>*/}
                {/*    <p className="text-gray-600">*/}
                {/*      We'll be in touch soon.*/}
                {/*    </p>*/}
                {/*  </div>*/}
                {/* ) : (*/}
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {successMsg && (
                      <div className={`flex items-center justify-center h-6 mt-3 bg-yellow-3000 text-center ${messageColor}`}>
                        {successMsg && <p>{successMsg}</p>}
                      </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 inline" htmlFor="Name">{lang('name')}</label>
                        {errors.name && <p className="text-red-500 ml-3 text-sm inline">{errors.name}</p>}
                        <input
                          type="text"
                          id="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder={lang('name')}
                          className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 inline">{lang('email')}</label>
                        {errors.email && <p className="text-red-500 ml-3 text-sm inline">{errors.email}</p>}
                        <input
                          type="email"
                          placeholder={lang('email')}
                          id="Email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 inline">{lang('phone')}</label>
                        {errors.phone && <p className="text-red-500 ml-3 text-sm inline">{errors.phone}</p>}
                        <input
                          type="text"
                          placeholder={lang('phone')}
                          id="Phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700 mb-2 inline">{lang('service')}</label>
                        {errors.service && <p className="text-red-500 ml-3 text-sm inline">{errors.service}</p>}
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full border border-gray-200 rounded-2xl px-4 py-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-transparent transition-all"
                        >
                          <option value="">Select a service</option>
                          <option value={t('common.webApplicationDevelopment')}>{t('common.webApplicationDevelopment')}</option>
                          <option value={t('common.mobileApplicationDevelopment')}>{t('common.mobileApplicationDevelopment')}</option>
                          <option value={t('common.erpSolutions')}>{t('common.erpSolutions')}</option>
                          <option value={t('common.customizedSoftware')}>{t('common.customizedSoftware')}</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 inline">{lang('message')}</label>
                      {errors.message && <p className="text-red-500 ml-3 text-sm inline">{errors.message}</p>}
                      <textarea
                        rows={5}
                        placeholder={lang('message')}
                        id="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-transparent transition-all resize-none"
                      />
                    </div>
                    {errors.verification && <p className="text-red-500 ml-3 text-sm inline">{errors.verification}</p>}
                    <Turnstile
                      key={turnstileKey}
                      sitekey={process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY}
                      onVerify={(t) => {
                        setToken(t);
                      }}
                      onError={() => setToken('')}
                      onExpire={() => setToken('')}
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full !text-center bg-theme-color text-white py-4 rounded-2xl font-black text-base hover:bg-blue-700 hover:shadow-xl hover:shadow-theme-color/30 transition-all">
                      {isSubmitting ? 'Sending...' : t('common.submit')}
                    </button>

                  </form>
                {/* )}*/}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
